/* Same-device mobile-home reading preferences only; never redirects to another page. */
function initializeHomeResume() {
  const root = document.querySelector('[data-mg-guide-filter][data-mobile-home]');
  if (!root || root.dataset.resumeReady || root.dataset.ready === 'true') return;
    const feedAbort = new AbortController();
    const feedFrames = new Set(), feedTimers = new Set(), feedObservers = new Set();
    const feedActive = () => root.isConnected && root.dataset.homeFeedExpired !== 'true';
    function feedListen(target, name, callback, options = {}) {
      target.addEventListener(name, callback, { ...(typeof options === 'boolean' ? { capture: options } : options), signal: feedAbort.signal });
    }
    function feedFrame(callback) {
      if (!feedActive()) return 0;
      const id = requestAnimationFrame(() => { feedFrames.delete(id); if (feedActive()) callback(); });
      feedFrames.add(id); return id;
    }
    function feedTimeout(callback, delay) {
      if (!feedActive()) return 0;
      const id = setTimeout(() => { feedTimers.delete(id); if (feedActive()) callback(); }, delay);
      feedTimers.add(id); return id;
    }
    function feedClearTimeout(id) {
      feedTimers.delete(id); clearTimeout(id);
    }
    function feedObserver(Type, callback, options) {
      const observer = new Type((...args) => { if (feedActive()) callback(...args); }, options);
      feedObservers.add(observer); return observer;
    }
    root.addEventListener('mg:home-feed-dispose', () => {
      feedAbort.abort();
      feedFrames.forEach(cancelAnimationFrame);
      feedTimers.forEach(clearTimeout);
      feedObservers.forEach(observer => observer.disconnect());
    }, { once: true });
  root.dataset.resumeReady = 'true';
  const mobile = window.matchMedia('(max-width: 749px)');
  const cards = [...root.querySelectorAll(':scope > .cards > [data-guide]')];
  if (Number(root.dataset.catalogueTotal) !== cards.length || !cards.length) return;
  const controls = [...root.querySelectorAll('[data-filter]')];
  const language = (document.documentElement.lang || 'en').split('-')[0];
  const key = 'mg-home-resume:v1:' + location.pathname;
  const ttl = 24 * 60 * 60 * 1000;
  let ready = false;
  let pending = false;
  let saveTimer;
  let noticeTimer;
  let notice;
  let revision = 0;
  let lastAnchor = null;
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  const pathOf = card => {
    try {
      const link = card.querySelector('a[href]');
      const url = new URL(link?.getAttribute('href') || '', location.href);
      return url.origin === location.origin && /\/blogs\/[^/]+\/[^/]+\/?$/.test(url.pathname) ? url.pathname + (card.dataset.productAsin ? '#asin=' + card.dataset.productAsin : '') : null;
    } catch (_) { return null; }
  };
  const paths = new Map(cards.map(card => [card, pathOf(card)]));
  const matches = (card, filters) => controls.every(control => {
    const value = filters[control.dataset.filter] || 'all';
    return value === 'all' || card.dataset.tags.split('|').includes(control.dataset.filter + ':' + value);
  });
  function validFilters(filters) {
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) return null;
    const result = {};
    for (const control of controls) {
      const name = control.dataset.filter;
      const value = filters[name] || 'all';
      if (typeof value !== 'string' || ![...control.options].some(option => option.value === value)) return null;
      if (value !== 'all' && !cards.some(card => card.dataset.tags.split('|').includes(name + ':' + value))) return null;
      result[name] = value;
    }
    return result;
  }
  function readRecord() {
    try {
      const raw = localStorage.getItem(key);
      if (!raw || raw.length > 4096) return null;
      const record = JSON.parse(raw);
      const age = Date.now() - record.savedAt;
      const filters = validFilters(record.filters);
      if (record.version !== 1 || !Number.isFinite(record.savedAt) || age < 0 || age >= ttl || !filters || !Number.isSafeInteger(record.loaded) || record.loaded < 6) return null;
      if (record.anchor !== null && (!record.anchor || typeof record.anchor.path !== 'string' || record.anchor.path.length > 500 || !Number.isFinite(record.anchor.offset) || Math.abs(record.anchor.offset) > 3000)) return null;
      return { ...record, filters };
    } catch (_) { return null; } // Storage can be blocked or cleared; browsing still works.
  }
  function headerBottom() {
    const header = document.querySelector('#header-component');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight ? Math.min(rect.bottom, window.innerHeight) : 0;
  }
  function positionNotice() {
    if (!notice) return false;
    const categories = root.querySelector('[data-mobile-categories]');
    const rect = categories?.getBoundingClientRect();
    const top = headerBottom();
    // A very tall header/short viewport leaves no safe place for a transient notice.
    if (window.innerHeight - top < 120) { removeNotice(); return false; }
    const belowCategories = rect && rect.top >= top && rect.bottom < window.innerHeight - 90;
    notice.style.top = Math.round(belowCategories ? rect.bottom + 6 : top + 8) + 'px';
    return true;
  }
  root.addEventListener('mg:home-feed-dispose', removeNotice, { once: true });
  function removeNotice() {
    feedClearTimeout(noticeTimer);
    notice?.remove();
    notice = null;
  }
  function showNotice() {
    removeNotice();
    notice = document.createElement('p');
    notice.className = 'mg-home-resume-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.setAttribute('aria-atomic', 'true');
    document.body.append(notice);
    if (!positionNotice()) return;
    const currentNotice = notice;
    feedFrame(() => {
      if (notice !== currentNotice) return;
      notice.textContent = ({ en: 'Back at your last browsing position.', zh: '已回到上次浏览位置', es: 'Has vuelto a tu última posición de lectura.' })[language] || 'Back at your last browsing position.';
      noticeTimer = feedTimeout(removeNotice, 3000);
    });
  }
  function currentFilters() {
    return Object.fromEntries(controls.map(control => [control.dataset.filter, control.value || 'all']));
  }
  function saveRecord() {
    if (!ready || pending || !mobile.matches || root.dataset.mobileReady !== 'true') return;
    const filters = validFilters(currentFilters());
    if (!filters) return;
    const visible = cards.filter(card => !card.hidden);
    const top = headerBottom() + 8;
    const anchor = visible.find(card => {
      const rect = card.getBoundingClientRect();
      return rect.bottom > top + 24 && rect.top < window.innerHeight - 70;
    });
    if (anchor && paths.get(anchor)) lastAnchor = { path: paths.get(anchor), offset: Math.round(anchor.getBoundingClientRect().top) };
    else if (!visible.length || visible[0].getBoundingClientRect().top >= window.innerHeight - 70) lastAnchor = null;
    // At the end of the feed retain the most recently visible guide, not footer text.
    if (lastAnchor && !visible.some(card => paths.get(card) === lastAnchor.path)) lastAnchor = null;
    const requested = Number(new URLSearchParams(location.hash.slice(1)).get('loaded'));
    const loaded = Math.max(6, Math.min(cards.length, Number.isSafeInteger(requested) && requested >= 6 ? requested : visible.length));
    try { localStorage.setItem(key, JSON.stringify({ version: 1, savedAt: Date.now(), filters, loaded, anchor: lastAnchor })); } catch (_) {}
  }
  function scheduleSave() {
    feedClearTimeout(saveTimer);
    saveTimer = feedTimeout(saveRecord, 250);
  }
  function finishPending() {
    pending = false;
    delete root.dataset.resumePending;
    ready = true;
  }
  function cancelPending(event) {
    if (!pending || !event.isTrusted) return;
    revision++;
    finishPending();
  }
  // An actual interaction wins over a deferred restoration; never pull the reader back.
  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(name => feedListen(window, name, cancelPending, { passive: true }));
  feedListen(window, 'scroll', () => { positionNotice(); scheduleSave(); }, { passive: true });
  feedListen(window, 'resize', positionNotice, { passive: true });
  feedListen(root, 'click', scheduleSave);
  feedListen(root, 'change', scheduleSave);
  feedListen(window, 'hashchange', () => { if (pending) { revision++; finishPending(); } removeNotice(); scheduleSave(); });
  feedListen(document, 'visibilitychange', () => { if (document.visibilityState === 'hidden') saveRecord(); });
  feedListen(window, 'pagehide', () => { saveRecord(); revision++; pending = false; delete root.dataset.resumePending; ready = false; removeNotice(); });
  feedListen(window, 'pageshow', event => {
    if (!event.persisted && navigationType !== 'back_forward') return;
    ready = false;
    // A's native Back restoration remains the sole scroll owner, including bfcache.
    feedFrame(() => feedFrame(() => feedFrame(() => { ready = true; })));
  });
  feedListen(mobile, 'change', () => {
    if (!mobile.matches) { revision++; finishPending(); removeNotice(); }
  });

  const query = new URLSearchParams(location.search);
  const explicit = root.dataset.homeReset === 'true' || Boolean(location.hash) || ['page', 'loaded', 'q', ...controls.map(control => control.dataset.filter)].some(name => query.has(name));
  const record = mobile.matches && !explicit && navigationType !== 'back_forward' && navigationType !== 'reload' ? readRecord() : null;
  let target = null;
  let offset = 0;
  if (record) {
    const matching = cards.filter(card => matches(card, record.filters));
    target = record.anchor ? matching.find(card => paths.get(card) === record.anchor.path) : null;
    const index = target ? matching.indexOf(target) : -1;
    const loaded = Math.max(6, Math.min(matching.length, Math.max(record.loaded, Math.ceil((index + 1) / 6) * 6)));
    const hash = new URLSearchParams();
    Object.entries(record.filters).forEach(([name, value]) => { if (value !== 'all') hash.set(name, value); });
    // loaded=6 deliberately overrides any older sessionStorage state on an unqualified visit.
    hash.set('loaded', String(loaded));
    try {
      history.replaceState(history.state, '', location.pathname + location.search + '#' + hash.toString());
      pending = Boolean(target);
      if (pending) root.dataset.resumePending = 'true';
      offset = record.anchor?.offset || 0;
    } catch (_) { target = null; pending = false; }
  }
  function afterFeedReady() {
    if (!target || !pending) { ready = true; return; }
    const token = revision;
    const perform = () => feedFrame(() => feedFrame(() => {
      if (token !== revision || !pending) return;
      if (!mobile.matches || document.visibilityState === 'hidden') { finishPending(); return; }
      if (root.dataset.mobileReady !== 'true' || target.hidden) { finishPending(); return; }
      const rect = target.getBoundingClientRect();
      // A changed font, card height or viewport must still leave part of this guide visible.
      offset = Math.max(headerBottom() + 32 - rect.height, Math.min(offset, window.innerHeight - 70));
      const y = Math.max(0, window.scrollY + rect.top - offset);
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      feedFrame(() => feedFrame(() => {
        if (token !== revision || !pending) return;
        const success = !target.hidden && Math.abs(target.getBoundingClientRect().top - offset) <= 4;
        finishPending();
        if (success) { lastAnchor = { path: paths.get(target), offset }; showNotice(); }
      }));
    }));
    // Avoid a later font swap moving the chosen title; no network request is initiated.
    if (document.fonts?.ready) {
      let started = false;
      const once = () => { if (!started) { started = true; perform(); } };
      document.fonts.ready.then(once, once);
      feedTimeout(once, 700);
    } else perform();
  }
  if (root.dataset.ready === 'true') afterFeedReady();
  else feedListen(root, 'mg:catalogue-ready', afterFeedReady, { once: true });
}
if (window.MGHomeProducts) document.addEventListener( 'mg:home-products-mounted', initializeHomeResume, { once: true });
else initializeHomeResume();
